/**
 * GDPR-compliant cookie consent.
 *
 * - Three granular categories: strictly_necessary (always on), analytics, marketing.
 * - Consent choice is stored locally (localStorage) AND logged server-side for audit.
 * - PostHog and any "marketing" tooling must read hasConsent(cat) BEFORE running.
 * - Users can change their mind anytime via the CookieSettings modal (Footer link).
 *
 * IMPORTANT: do not init any non-essential tooling on page load without
 * checking hasConsent("analytics") / hasConsent("marketing") first.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "lf_consent";
const LISTENER_KEY = "__lf_consent_listeners__";

export const DEFAULT_CHOICE = {
  strictly_necessary: true,
  analytics: false,
  marketing: false,
};

function getAnonymousId() {
  let id = localStorage.getItem("lf_anon_id");
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    localStorage.setItem("lf_anon_id", id);
  }
  return id;
}

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch {
    return null;
  }
}

export function hasConsent(category) {
  const c = readConsent();
  if (!c) return false;
  if (category === "strictly_necessary") return true;
  return !!c.choice?.[category];
}

export function hasDecided() {
  return !!readConsent();
}

function notifyListeners(choice) {
  const listeners = window[LISTENER_KEY] || [];
  listeners.forEach((fn) => {
    try { fn(choice); } catch { /* noop */ }
  });
}

export function onConsentChange(fn) {
  if (typeof window === "undefined") return () => {};
  window[LISTENER_KEY] = window[LISTENER_KEY] || [];
  window[LISTENER_KEY].push(fn);
  return () => {
    window[LISTENER_KEY] = (window[LISTENER_KEY] || []).filter((x) => x !== fn);
  };
}

export async function saveConsent(choice, source = "banner") {
  const normalized = {
    strictly_necessary: true,
    analytics: !!choice.analytics,
    marketing: !!choice.marketing,
  };
  const record = {
    choice: normalized,
    saved_at: new Date().toISOString(),
    source,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch { /* noop */ }
  // Best-effort server-side audit log
  try {
    await axios.post(`${API}/consent`, {
      anonymous_id: getAnonymousId(),
      choice: normalized,
      source,
    });
  } catch { /* noop */ }
  notifyListeners(record);
  return record;
}

export function revokeConsent() {
  localStorage.removeItem(STORAGE_KEY);
  notifyListeners(null);
}
